import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import {
  portfolioService,
  type PortfolioAccessRequestDto,
  type PortfolioSettingsDto,
} from '@/services/portfolio';
import { Globe, RefreshCw, CheckCircle2, XCircle } from 'lucide-react';
import {
  PromoCard,
  PromoCardBody,
  PromoCardDesc,
  PromoCardHeader,
  PromoCardTitle,
} from '@/components/promo/PromoCard';
import { getMarketingSiteOrigin, getPublicPortfolioUrl } from '@/utils/hostOrigins';

const TeacherPortfolioPage: React.FC = () => {
  const { toast } = useToast();
  const [settings, setSettings] = useState<PortfolioSettingsDto>({
    slug: '',
    slugLocked: false,
    headline: '',
    aboutText: '',
    introVideoUrl: '',
    pricingText: '',
    ctaText: 'Request access',
    isPublished: false,
  });
  const [accessRequests, setAccessRequests] = useState<PortfolioAccessRequestDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [processingId, setProcessingId] = useState<string | null>(null);

  const publicUrl = useMemo(
    () => getPublicPortfolioUrl(settings.slug ?? ''),
    [settings.slug]
  );

  useEffect(() => {
    void loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [portfolio, requests] = await Promise.all([
        portfolioService.getMyPortfolio(),
        portfolioService.getAccessRequests(),
      ]);
      setSettings({
        slug: portfolio.slug ?? '',
        slugLocked: Boolean(portfolio.slugLocked),
        headline: portfolio.headline ?? '',
        aboutText: portfolio.aboutText ?? '',
        introVideoUrl: portfolio.introVideoUrl ?? '',
        pricingText: portfolio.pricingText ?? '',
        ctaText: portfolio.ctaText ?? 'Request access',
        isPublished: Boolean(portfolio.isPublished),
      });
      setAccessRequests(Array.isArray(requests) ? requests : []);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.response?.data?.error || 'Failed to load portfolio data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const updated = await portfolioService.saveMyPortfolio({
        slug: settings.slug.trim().toLowerCase(),
        headline: settings.headline || undefined,
        aboutText: settings.aboutText || undefined,
        introVideoUrl: settings.introVideoUrl || undefined,
        pricingText: settings.pricingText || undefined,
        ctaText: settings.ctaText || undefined,
        isPublished: settings.isPublished,
      });
      setSettings({
        slug: updated.slug ?? '',
        slugLocked: Boolean(updated.slugLocked),
        headline: updated.headline ?? '',
        aboutText: updated.aboutText ?? '',
        introVideoUrl: updated.introVideoUrl ?? '',
        pricingText: updated.pricingText ?? '',
        ctaText: updated.ctaText ?? 'Request access',
        isPublished: Boolean(updated.isPublished),
      });
      toast({
        title: 'Saved',
        description: 'Portfolio settings updated successfully',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.response?.data?.error || 'Failed to save portfolio settings',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDecision = async (requestId: string, decision: 'approve' | 'deny') => {
    try {
      setProcessingId(requestId);
      if (decision === 'approve') {
        await portfolioService.approveAccessRequest(requestId);
      } else {
        await portfolioService.denyAccessRequest(requestId);
      }
      await loadData();
      toast({
        title: 'Updated',
        description: `Request ${decision === 'approve' ? 'approved' : 'denied'} successfully`,
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.response?.data?.error || `Failed to ${decision} request`,
        variant: 'destructive',
      });
    } finally {
      setProcessingId(null);
    }
  };

  const brand = settings.headline?.trim() || settings.slug || 'Your studio';
  const domain = publicUrl
    ? publicUrl.replace(/^https?:\/\//, '')
    : getMarketingSiteOrigin().replace(/^https?:\/\//, '');

  return (
    <div className="space-y-4">
      <PromoCard>
        <PromoCardHeader>
          <PromoCardTitle icon={<Globe size={20} strokeWidth={2} />}>Website</PromoCardTitle>
          <PromoCardDesc>
            Your public site stays on your domain — lessons, posts, and reels in one branded place.
          </PromoCardDesc>
        </PromoCardHeader>
        <PromoCardBody>
          <div className="public-site-preview">
            <header className="pub-nav">
              <span className="pub-brand">{brand}</span>
              <div className="pub-links">
                <span>Lessons</span>
                <span>Plans</span>
                <span>Blog</span>
                <span>Book</span>
              </div>
            </header>
            <div className="pub-hero">
              <h1>{settings.headline || 'Piano & guitar lessons'}</h1>
              <p>
                {settings.aboutText?.slice(0, 120) ||
                  'Your public site — lessons, posts, and booking in one branded place.'}
              </p>
              <span className="pub-cta">{settings.ctaText || 'Book your first lesson'}</span>
            </div>
            <footer className="pub-foot">{domain} · white labeled portfolio</footer>
          </div>
        </PromoCardBody>
      </PromoCard>

      <PromoCard>
        <PromoCardHeader>
          <PromoCardTitle icon={<Globe size={20} strokeWidth={2} />}>Site settings</PromoCardTitle>
          <PromoCardDesc>
            Slug is fixed after first save. Publish when you are ready for visitors.
          </PromoCardDesc>
        </PromoCardHeader>
        <PromoCardBody className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-foreground">Public slug</Label>
              <Input
                value={settings.slug}
                onChange={(event) => setSettings((prev) => ({ ...prev, slug: event.target.value }))}
                placeholder="teacher-name-studio"
                disabled={settings.slugLocked || loading}
                className="bg-slate-50 border-slate-200 text-foreground disabled:opacity-60 disabled:cursor-not-allowed"
              />
              {settings.slugLocked ? (
                <p className="text-xs text-gray-500 mt-1">
                  This URL is locked. You can edit other sections below anytime.
                </p>
              ) : (
                <p className="text-xs text-amber-200/90 mt-1">
                  Choose carefully — once you save, this address cannot be changed.
                </p>
              )}
            </div>
            <div className="flex items-end gap-2">
              <Button
                variant={settings.isPublished ? 'destructive' : 'default'}
                onClick={() => setSettings((prev) => ({ ...prev, isPublished: !prev.isPublished }))}
                className="w-full md:w-auto"
              >
                {settings.isPublished ? 'Unpublish' : 'Publish'}
              </Button>
              <Button variant="outline" className="border-white/20" onClick={() => void loadData()} disabled={loading}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>

          {Boolean(settings.slug?.trim()) && !settings.isPublished && (
            <div className="rounded-md border border-amber-500/35 bg-amber-500/10 px-3 py-2 text-sm text-amber-50">
              Visitors only see this URL after you tap <strong>Publish</strong> and then <strong>Save Portfolio</strong>.
            </div>
          )}

          <div>
            <Label className="text-foreground">Headline</Label>
            <Input
              value={settings.headline ?? ''}
              onChange={(event) => setSettings((prev) => ({ ...prev, headline: event.target.value }))}
              placeholder="Teach music with confidence"
              className="bg-slate-50 border-slate-200 text-foreground"
            />
          </div>

          <div>
            <Label className="text-foreground">About</Label>
            <Textarea
              value={settings.aboutText ?? ''}
              onChange={(event) => setSettings((prev) => ({ ...prev, aboutText: event.target.value }))}
              placeholder="Describe your experience, lessons and teaching style..."
              className="bg-slate-50 border-slate-200 text-foreground min-h-[120px]"
            />
          </div>

          <div>
            <Label className="text-foreground">Intro video URL</Label>
            <Input
              value={settings.introVideoUrl ?? ''}
              onChange={(event) => setSettings((prev) => ({ ...prev, introVideoUrl: event.target.value }))}
              placeholder="https://www.youtube.com/watch?v=… or Vimeo / Loom embed link"
              className="bg-slate-50 border-slate-200 text-foreground"
            />
          </div>

          <div>
            <Label className="text-foreground">Pricing</Label>
            <Textarea
              value={settings.pricingText ?? ''}
              onChange={(event) => setSettings((prev) => ({ ...prev, pricingText: event.target.value }))}
              placeholder="Starter: 40 EUR / lesson ..."
              className="bg-slate-50 border-slate-200 text-foreground min-h-[100px]"
            />
          </div>

          <div>
            <Label className="text-foreground">Call to action text</Label>
            <Input
              value={settings.ctaText ?? ''}
              onChange={(event) => setSettings((prev) => ({ ...prev, ctaText: event.target.value }))}
              placeholder="Request access"
              className="bg-slate-50 border-slate-200 text-foreground"
            />
          </div>

          {publicUrl && (
            <div className="rounded border border-slate-200 bg-slate-100 px-3 py-2 space-y-2">
              <p className="text-xs text-gray-400">Public URL</p>
              <p className="text-sm text-foreground break-all">{publicUrl}</p>
              <Button
                type="button"
                variant="outline"
                className="border-white/20"
                onClick={() => window.open(publicUrl, '_blank', 'noopener,noreferrer')}
              >
                Open public portfolio
              </Button>
            </div>
          )}

          <Button onClick={handleSave} disabled={saving || loading}>
            {saving ? 'Saving…' : 'Save Portfolio'}
          </Button>
        </PromoCardBody>
      </PromoCard>

      <PromoCard>
        <PromoCardHeader>
          <PromoCardTitle>Access requests</PromoCardTitle>
          <PromoCardDesc>
            Visitors can submit a contact form on your portfolio. Approve or deny from here.
          </PromoCardDesc>
        </PromoCardHeader>
        <PromoCardBody>
          {loading ? (
            <p className="text-gray-400">Loading requests…</p>
          ) : accessRequests.length === 0 ? (
            <p className="text-gray-400">No access requests yet.</p>
          ) : (
            <div className="space-y-2">
              {accessRequests.map((request) => (
                <div
                  key={request.id}
                  className="rounded-lg border border-slate-200 bg-slate-50 p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                >
                  <div>
                    <p className="text-sm text-foreground font-medium">
                      {[request.firstName, request.lastName].filter(Boolean).join(' ') || 'Unknown'}
                    </p>
                    <p className="text-xs text-gray-400">{request.email}</p>
                    {request.message && <p className="text-xs text-gray-300 mt-1">{request.message}</p>}
                    <p className="text-[11px] text-gray-500 mt-1">Requested: {new Date(request.createdAt).toLocaleString()}</p>
                    {request.invitationUrl && (
                      <p className="text-[11px] text-purple-300 mt-1 break-all">Invitation: {request.invitationUrl}</p>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`text-xs px-2 py-1 rounded border ${
                        request.status === 'approved'
                          ? 'bg-green-500/20 text-green-300 border-green-500/30'
                          : request.status === 'denied'
                          ? 'bg-red-500/20 text-red-300 border-red-500/30'
                          : 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30'
                      }`}
                    >
                      {request.status.toUpperCase()}
                    </span>
                    {request.status === 'pending' && (
                      <>
                        <Button size="sm" onClick={() => void handleDecision(request.id, 'approve')} disabled={processingId === request.id}>
                          <CheckCircle2 className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => void handleDecision(request.id, 'deny')}
                          disabled={processingId === request.id}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Deny
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </PromoCardBody>
      </PromoCard>
    </div>
  );
};

export default TeacherPortfolioPage;
